import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createGroup } from '@/lib/firebase/queries/groups';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { AVAILABLE_CATEGORIES, GROUP_CATEGORIES } from '@/lib/constants/groupCategories';
import type { GroupCategory } from '@/types/support';
import { clearGroupsCache } from '@/lib/firebase/queries/groups';

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (createdGroup?: any) => void;
}

export function CreateGroupDialog({ open, onOpenChange, onSuccess }: CreateGroupDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<GroupCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !description.trim() || !currentUser || !userData || selectedCategories.length === 0) return;

    try {
      setIsLoading(true);
      const createdGroup = await createGroup({
        name: name.trim(),
        description: description.trim(),
        categories: selectedCategories,
        isPrivate,
        creatorId: currentUser.uid,
        creatorName: `${userData.firstNameEn} ${userData.lastNameEn}`,
        groupType: 'subgroup' // Customer-created groups are subgroups by default
      });

      if (createdGroup) {
        // Clear cache to force fresh data
        clearGroupsCache();
        
        // Pass the created group to onSuccess for optimistic update
        toast.success(t('support.groupCreated'));
        setName('');
        setDescription('');
        setIsPrivate(false);
        setSelectedCategories([]);
        
        // Close dialog immediately - the group will appear via optimistic update
        onSuccess?.(createdGroup);
        onOpenChange(false);
      } else {
        toast.error(t('support.groupCreationError'));
      }
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error(t('support.groupCreationError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen sm:w-[90vw] sm:max-w-2xl sm:h-auto">
        <DialogHeader>
          <DialogTitle>{t('support.createNewGroup')}</DialogTitle>
          <DialogDescription>
            {t('support.selectCategories')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">{t('support.groupName')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('support.groupNamePlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('support.groupDescription')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('support.groupDescriptionPlaceholder')}
              className="min-h-[100px]"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-4">
            <Label>{t('support.groupCategories')}</Label>
            <div className="grid grid-cols-2 gap-4">
              {AVAILABLE_CATEGORIES.map((category) => (
                <div key={category} className="flex items-center space-x-2">
                  <Checkbox
                    id={category}
                    checked={selectedCategories.includes(category)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedCategories(prev => [...prev, category]);
                      } else {
                        setSelectedCategories(prev => prev.filter(c => c !== category));
                      }
                    }}
                  />
                  <label
                    htmlFor={category}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    <span>{GROUP_CATEGORIES[category]}</span> {t(`support.categories.${category}`)}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="private">{t('support.isPrivate')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('support.isPrivateDescription')}
              </p>
            </div>
            <Switch
              id="private"
              checked={isPrivate}
              onCheckedChange={setIsPrivate}
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || !name.trim() || !description.trim() || selectedCategories.length === 0}
              className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('support.creating')}
                </>
              ) : (
                t('support.createGroup')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}